import { useEffect, useRef } from 'react';

import 'mutationobserver-shim';
import ResizeObserver from 'resize-observer-polyfill';
import { ESdkEvents } from '../../../contracts';
import { sendFEEvent } from '../../../eventUtil';
import { useDebounce } from '../../../hooks/use-debounce';
import { useDeviceHelper } from '../../../hooks/use-device-helper';
import { getIsSupercellTemplate } from '../../../utils/get-is-supercell-template';

const IS_SUPERCELL_TEMPLATE = getIsSupercellTemplate();

export function useResizeObserver() {
  const ref = useRef<HTMLElement | null>(null);
  const descriptionRef = useRef<HTMLElement | null>(null);
  const lastHeightRef = useRef<number>(0);
  const lastOverflowStateRef = useRef<boolean>(false);
  const { isMobile } = useDeviceHelper();

  const sendHeight = () => {
    const target = ref.current;
    if (!target) return;

    const height = target.scrollHeight;
    const isOverflowing = target.scrollHeight > target.offsetHeight;

    // Get description height on mobile
    let descriptionHeight = 0;
    if (isMobile && descriptionRef.current) {
      descriptionHeight = descriptionRef.current.offsetHeight;
    }

    const totalHeight = height + descriptionHeight;

    // Send height if it changed OR if overflow state changed
    if (totalHeight !== lastHeightRef.current || isOverflowing !== lastOverflowStateRef.current) {
      lastHeightRef.current = totalHeight;
      lastOverflowStateRef.current = isOverflowing;
      const newHeight = isMobile ? totalHeight : totalHeight;
      sendFEEvent(ESdkEvents.CHECKOUT_DIMENSIONS_CHANGED, {
        height: Math.max(newHeight, 770),
      } as any);
    }
  };

  // Reduced debounce timing for more responsive updates
  const debouncedSendHeight = useDebounce(sendHeight, 50);

  useEffect(() => {
    const target = ref.current;
    if (!target || !IS_SUPERCELL_TEMPLATE) return;

    sendHeight(); // Initial

    const resizeObserver = new ResizeObserver(debouncedSendHeight);
    resizeObserver.observe(target);

    const mutationObserver = new MutationObserver(debouncedSendHeight);
    mutationObserver.observe(target, { childList: true, subtree: true });

    // Also observe description component on mobile
    if (isMobile && descriptionRef.current) {
      resizeObserver.observe(descriptionRef.current);
      mutationObserver.observe(descriptionRef.current, { childList: true, subtree: true });
    }

    // Enabled overflow checker as fallback for missed resize events
    const overflowChecker = setInterval(() => {
      const isCurrentlyOverflowing = target.scrollHeight > target.offsetHeight;
      if (isCurrentlyOverflowing !== lastOverflowStateRef.current) {
        sendHeight(); // Don't debounce this one - overflow is important
      }
    }, 1000);

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      clearInterval(overflowChecker);
    };
  }, [debouncedSendHeight, isMobile]);

  return { paymentRef: ref, descriptionRef };
}
